/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, Output, EventEmitter, OnInit, APP_INITIALIZER, ViewChild } from "@angular/core";
import { ParameterValue } from "../simple-function-action-editor.component";
import { Event } from "app/models/event";
import { PropertyType } from "app/models/property-type";
import { AntennaInstance } from "app/models/antenna-instance";
import { AntennaService } from "app/services/antenna.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  selector: 'app-function-parameter-editor-antenna',
  templateUrl: './antenna-function-parameter-editor.component.html'
})
export class AntennaFunctionParameterEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Event Trigger used for the Rule
  @Input()
  set event(event: Event) {
    this._event = event;
    this.refreshSelectOptions(false, true);
  }
  get event(): Event {
    return this._event;
  }
  _event: Event;

  //Parameter Value being shown / represented
  @Input()
  parameterValue: ParameterValue;

  //Change Event fired when the value is changed.
  @Output()
  valueChange = new EventEmitter<void>();

  //Flag indicating whether the Select Options are being loaded
  loadingSelectOptions = true;

  //Options available for the Parameter Value
  antennaOptions: SelectOption[] = [];
  eventOptions: SelectOption[] = [];
  selectOptions: SelectOption[] = [];


  get selectedOption(): SelectOption {
    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      return this.selectOptions.find(option => option.mValue == this.parameterValue.mValue);
    }
    return null;
  }
  set selectedOption(option: SelectOption) {
    if (this.parameterValue != null) {
      this.parameterValue.mValue = option == null? null : option.mValue;
      this.valueChange.emit();
    }
  }



  constructor(
    private mAntennaService: AntennaService
  ) { }

  ngOnInit(): void {
    this.refreshSelectOptions(true, true);
  }



  async refreshSelectOptions(refreshAntennaOptions: boolean, refreshEventOptions: boolean): Promise<void> {
    this.loadingSelectOptions = true;

    if (refreshEventOptions) {
      await this.refreshEventOptions();
    }

    if (refreshAntennaOptions) {
      await this.refreshAntennaOptions();
    }

    const options: SelectOption[] = [];
    options.push(...this.eventOptions);
    options.push(...this.antennaOptions);

    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      if (options.find(option => option.mValue == this.parameterValue.mValue) == null) {
        const unknownValueOption = new SelectOption();
        unknownValueOption.mValue = this.parameterValue.mValue;
        unknownValueOption.mDisplay = "UNKNOWN VALUE => " + this.parameterValue.mValue;
        options.push(unknownValueOption);
      }
    }

    this.selectOptions = options;

    this.loadingSelectOptions = false;
  }

  private async refreshAntennaOptions(): Promise<void> {
    let antennas: AntennaInstance[];
    try {
      antennas = await this.mAntennaService.getAllAntennaInstances();
    } catch (error) {
      this.handleError(error);
    }

    if (antennas == null) {
      this.antennaOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const antenna of antennas) {
        const option = new SelectOption();
        option.mValue = antenna.id.toString();
        option.mDisplay = antenna.name;
        options.push(option);
      }
      this.antennaOptions = options;
    }
  }

  private async refreshEventOptions(): Promise<void> {
    const options: SelectOption[] = [];
    if (this.event != null) {
      if (this.event.properties != null) {
        for (const eventProperty of this.event.properties) {
          if (eventProperty.type == PropertyType.ANTENNA_REFERENCE) {
            const option = new SelectOption();
            option.mValue = "${event." + eventProperty.property + "}";
            option.mDisplay = "FROM EVENT => " + eventProperty.name;
            options.push(option);
          }
        }
      }
    }

    this.eventOptions = options;
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString())
    }
  }

}

class SelectOption {
  mValue: string;
  mDisplay: string;
}
